import pdfpng from '@assets/pdf.png';
import ImageGalleryModal from '@components/Shared/ImageGalleryModal';
import { UI_SIZE } from '@constants/ui';
import { useLocalization } from '@lib/localization';
import { loadPdfjs } from '@lib/pdf/pdfjs';
import { FOLDER_BASE } from '@lib/routing/projectPath';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import { Pagination, useMediaQuery, useTheme } from '@mui/material';
import { useAppSelector } from '@store/hooks';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { type ChangeEvent, type FC, useEffect, useState } from 'react';
import {
  AngleUpIcon, CheckIcon, CogIcon, CommentIcon, ExclamationIcon, SmsIcon, TimesIcon
} from 'react-line-awesome';
import { useNavigate } from 'react-router-dom';
import {
  CloseButton,
  CollapseButton,
  IconWrapper,
  ImgInfo,
  InfoContent,
  InfoLabel,
  InfoRow,
  InfoValue,
  ModalContentWrapper,
  ModalLeftSide,
  ModalRightSide,
  ModalTitle,
  NodeImage,
  NodeLeading,
  PaginationWrapper,
  ResourcesContainer,
  StyledModal,
  StyledModalBody,
  StyledModalHeader,
} from './InspectionDetail.styled';
import PoiCustomFields from './PoiCustomFields';

interface ImageResource {
  original: string
  thumbnail: string
  description: string
}

interface PdfResource {
  mainUrl: string
  thumbnailUrl: string
  description: string
}

const InspectionDetailModal: FC = () => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [animating, setAnimating] = useState<boolean>(false);
  const [imageModal, setImageModal] = useState(false);
  const [images, setImages] = useState<ImageResource[] | null>(null);
  const [imageStartIndex, setImageStartIndex] = useState<number | undefined>();
  const [pdfs, setPdfs] = useState<PdfResource[] | null>(null);
  const { base, inspectionId, poiId, buildPath } = useProjectRoute();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const { selectedPOI, stepIndex, inspection, isInspectionDetailModalOpen } = useAppSelector(state => state.viewer);
  // The same panel serves the project page and the standalone viewer; the URL family says which.
  const isOverview = base === FOLDER_BASE;

  const { t } = useLocalization();
  const noticeColors = useNoticeColors();

  const color: string = noticeColors[selectedPOI?.steps?.[stepIndex ?? 0]?.noticeType ?? 'NotChecked'];

  useEffect(() => {
    const _init = async () => {
      if (!inspection || !selectedPOI) {
        setImages(null);
        setPdfs(null);
        return;
      }

      const resources = await inspection.getResourcesByPoiId(selectedPOI.id, stepIndex);

      if (resources) {
        const imageResources = resources.filter((resource) => resource.type === 'Image');
        const pdfResources = resources.filter((resource) => resource.type === 'Pdf');

        setImages(
          imageResources.map((resource) => ({
            original: resource.resourceUrl,
            thumbnail: resource.resourceUrl,
            description: resource.name,
          }))
        );

        const pdfPreviews = await Promise.all(
          pdfResources.map(async (resource, pdfIndex) => {
            const pdfPreview = await getPDFPreview(resource.resourceUrl);
            if (pdfPreview) {
              return {
                mainUrl: resource.resourceUrl,
                thumbnailUrl: pdfPreview,
                description: `PDF Preview ${pdfIndex + 1} - ${resource.name}`,
              };
            } else {
              return null;
            }
          })
        );

        setPdfs(
          pdfPreviews.filter(
            (preview): preview is PdfResource => preview !== null
          )
        );
      } else {
        setImages(null);
        setPdfs(null);
      }
    };

    _init();
  }, [inspection, selectedPOI, stepIndex]);

  const openGallery = (index?: number) => {
    setImageStartIndex(index);
    setImageModal(true);
  };

  const handleChange = (_: ChangeEvent<unknown>, value: number) => {
    navigate(buildPath({ inspectionId, poiId, stepIndex: value - 1 }));
  };

  // Closing drops the point and stays on the inspection, in both families: the app side
  // used to land on the list because the inspection *was* the list, an accordion row that
  // unfolded. It has its own page now, and that page is where a closed point belongs.
  const handleClose = () => {
    navigate(buildPath({ inspectionId }));
  };

  const handleToggleCollapse = () => {
    setAnimating(true);
    setCollapsed(!collapsed);
    setTimeout(() => setAnimating(false), 300);
  };

  if (!isInspectionDetailModalOpen) {
    return null;
  }

  return (
    <>
      <StyledModal
        style={{
          marginTop: isOverview && !isLg ? UI_SIZE.APP_NAVBAR_HEIGHT : 0,
          marginLeft: isOverview && isLg ? UI_SIZE.DRAWER_WIDTH / 2 : 0,
          zIndex: theme.zIndex.modal
        }}
        size="lg"
        data-backdrop="false"
        className="inspection-detail-modal"
        show={isInspectionDetailModalOpen}
        backdrop={false}
        keyboard={false}
        onHide={handleClose}
      >
        <ModalContentWrapper style={{ maxHeight: collapsed ? '64px' : 'calc(100vh - 150px)' }}>
          <ModalLeftSide style={{ backgroundColor: color }}>
            <NodeLeading style={{ marginBottom: 4, marginTop: 4 }}>{selectedPOI?.order ?? 0}</NodeLeading>
            {!animating && !collapsed ? (
              <div>
                <NodeLeading
                  $bgColor={selectedPOI?.steps?.[stepIndex]?.noticeType === 'Ok' ? '#ffffff3d' : 'transparent'}
                >
                  <CheckIcon />
                </NodeLeading>
                <NodeLeading
                  $bgColor={selectedPOI?.steps?.[stepIndex]?.noticeType === 'Warning' ? '#ffffff3d' : 'transparent'}
                >
                  <ExclamationIcon />
                </NodeLeading>
                <NodeLeading
                  $bgColor={selectedPOI?.steps?.[stepIndex]?.noticeType === 'Error' ? '#ffffff3d' : 'transparent'}
                >
                  <TimesIcon />
                </NodeLeading>
              </div>
            ) : (
              <div></div>
            )}
          </ModalLeftSide>
          <ModalRightSide>
            <StyledModalHeader>
              <ModalTitle style={{ lineHeight: '2' }}>{selectedPOI?.name ?? '-'}</ModalTitle>
              <div>
                {!isOverview && (
                  <CollapseButton onClick={handleToggleCollapse} $collapsed={collapsed}>
                    <AngleUpIcon />
                  </CollapseButton>
                )}
                <CloseButton onClick={handleClose}>
                  <TimesIcon />
                </CloseButton>
              </div>
            </StyledModalHeader>
            <StyledModalBody>
              <InfoRow>
                <IconWrapper>
                  <CogIcon style={{ fill: '#9e9e9e' }} />
                </IconWrapper>
                <InfoContent>
                  <InfoLabel>{t('Common_Part')}</InfoLabel>
                  <InfoValue>{selectedPOI?.partId ?? '-'}</InfoValue>
                </InfoContent>
              </InfoRow>

              <InfoRow>
                <IconWrapper>
                  <SmsIcon style={{ fill: '#9e9e9e' }} />
                </IconWrapper>
                <InfoContent>
                  <InfoLabel>{t('Common_Description')}</InfoLabel>
                  <InfoValue>{selectedPOI?.steps[stepIndex]?.description ?? '-'}</InfoValue>
                </InfoContent>
              </InfoRow>

              <InfoRow>
                <IconWrapper>
                  <CommentIcon style={{ fill: '#9e9e9e' }} />
                </IconWrapper>
                <InfoContent>
                  <InfoLabel>{t('Common_Comment')}</InfoLabel>
                  <InfoValue>{selectedPOI?.steps[stepIndex]?.comment ?? '-'}</InfoValue>
                </InfoContent>
              </InfoRow>

              <PoiCustomFields step={selectedPOI?.steps[stepIndex]} />

              <ResourcesContainer
                style={{ maxHeight: pdfs && images && (pdfs.length > 0 || images.length > 0) ? '162px' : '0px' }}
              >
                {pdfs &&
                  pdfs.length > 0 &&
                  pdfs.map((pdf, index) => (
                    <NodeImage
                      key={index}
                      target="_blank"
                      href={pdf.mainUrl}
                      rel="noreferrer"
                      style={{ backgroundImage: `url(${pdf.thumbnailUrl})` }}
                    >
                      <ImgInfo>
                        <img src={pdfpng || '/placeholder.svg'} width="30px" alt="PDF" />
                      </ImgInfo>
                    </NodeImage>
                  ))}
                {images &&
                  images.length > 0 &&
                  images.map((image, index) => (
                    <NodeImage
                      onClick={() => {
                        openGallery(index);
                      }}
                      key={index}
                      target="_blank"
                      rel="noreferrer"
                      style={{ backgroundImage: `url(${image.thumbnail})` }}
                    />
                  ))}
              </ResourcesContainer>

              {selectedPOI && selectedPOI.steps.length > 1 && (
                <PaginationWrapper>
                  <Pagination
                    count={selectedPOI?.steps.length}
                    page={stepIndex + 1}
                    onChange={(_, value) => handleChange(_, value)}
                  />
                </PaginationWrapper>
              )}
            </StyledModalBody>
          </ModalRightSide>
        </ModalContentWrapper>
      </StyledModal>
      <ImageGalleryModal
        open={imageModal}
        onClose={() => setImageModal(false)}
        images={images ?? []}
        startIndex={imageStartIndex}
      />
    </>
  );
};


const getPDFPreview = async (url: string): Promise<string | null> => {
  try {
    // The library and its worker are loaded by the first attachment that needs them.
    const { getDocument } = await loadPdfjs();
    const loadingTask = getDocument(url);
    const pdfDocument = await loadingTask.promise;

    const firstPage = await pdfDocument.getPage(1);

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;
    const viewport = firstPage.getViewport({ scale: 1 });

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await firstPage.render(renderContext).promise;

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((blob) => resolve(blob)));

    if (!blob) {
      console.error('Error creating blob from canvas');
      return null;
    }

    const blobUrl = (URL || webkitURL).createObjectURL(blob);

    return blobUrl;
  } catch (error) {
    console.error('Error getting PDF preview:', error);
    return null;
  }
};

export default InspectionDetailModal;
